import type { ChapterData } from "../types/chapter";
import type { GameSave } from "../types/save";
import type { LevelData } from "../types/level";
import { buildGuessSequence, type NoteState } from "../model/NoteState";
import type {
  LevelSelectEdgeState,
  LevelSelectNodeState,
  LevelSelectState,
} from "../controller/LevelSelectController";
import { LayoutBitmapTextRenderer } from "./LayoutBitmapTextRenderer";
import { SymbolSequenceRenderer } from "./SymbolSequenceRenderer";
import {
  NOTE_OPTIONS_FONT_KEY,
  NOTE_OPTIONS_FONT_SIZE,
  SYMBOL_SEQUENCE_FONT_CONFIGS,
} from "./SymbolSequenceFont";

const CELL_SIZE = 36;
const NODE_SIZE = 18;
const VIEWPORT_WIDTH = 384;
const VIEWPORT_HEIGHT = 216;

const PALETTE = {
  nodeLocked: 0x666666,
  nodeUnlocked: 0x4fc3f7,
  nodeCleared: 0x66bb6a,
  nodePassed: 0x9c27b0,
  nodePerfected: 0xffd700,
  edgeUnlocked: 0xaaaaaa,
  edgeLocked: 0x444444,
  cursorNormal: 0xffffff,
  cursorGreen: 0x66bb6a,
  cursorRed: 0xff5252,
  groupCompleted: 0x66bb6a,
  groupGray: 0x888888,
  bridgeLockLocked: 0xff5252,
  bridgeLockUnlocked: 0x66bb6a,
  passwordFlashBlue: 0x42a5f5,
  passwordSuccessLightGreen: 0x90ee90,
} as const;

const CHAPTER_GROUP_COLORS: readonly number[] = [
  0x26c6da,
  0xff9800,
  0x536dfe,
  0xff4081,
  0x673ab7,
  0xff5722,
];

const GROUP_SQUARE_SIZE = 4;
const GROUP_SQUARE_INSET = 2;
const GROUP_SQUARE_CORNER_OFFSET = 4;
const BRIDGE_PROGRESS_CHIP_SIZE = 4;
const PASSWORD_STATUS_Y = 138;
const PASSWORD_SEQUENCE_TOP_Y = 160;
const PASSWORD_STATUS_BG_PADDING_X = 6;
const PASSWORD_STATUS_BG_PADDING_Y = 4;
const PASSWORD_STATUS_BG_ALPHA = 0.6;
const PASSWORD_STATUS_BG_DEPTH = 13;
const PASSWORD_STATUS_TEXT_DEPTH = 14;

export interface LevelSelectRenderOptions {
  readonly cursorOffsetX?: number;
  readonly cursorOffsetY?: number;
  readonly cursorRed?: boolean;
  readonly chapters?: readonly ChapterData[];
  readonly save?: GameSave | undefined;
  readonly levels?: Readonly<Record<string, LevelData>>;
  readonly passwordInputFlash?: boolean;
}

export class LevelSelectRenderer {
  private readonly scene: Phaser.Scene;
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly statusBackground: Phaser.GameObjects.Rectangle;
  private readonly statusText: LayoutBitmapTextRenderer;
  private readonly bridgeLabels: Map<string, Phaser.GameObjects.BitmapText>;
  private readonly symbolSequenceRenderer: SymbolSequenceRenderer;
  private nodePositions: Map<string, { x: number; y: number }>;

  public constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.graphics = scene.add.graphics();
    this.statusBackground = scene.add.rectangle(0, 0, 0, 0, 0x000000, PASSWORD_STATUS_BG_ALPHA);
    this.statusBackground.setScrollFactor(0);
    this.statusBackground.setDepth(PASSWORD_STATUS_BG_DEPTH);
    this.statusBackground.setVisible(false);
    this.statusText = new LayoutBitmapTextRenderer(scene, {
      depth: PASSWORD_STATUS_TEXT_DEPTH,
      tint: 0xffffff,
      fontProfile: SYMBOL_SEQUENCE_FONT_CONFIGS["10px"],
    });
    this.statusText.setVisible(false);
    this.bridgeLabels = new Map();
    this.symbolSequenceRenderer = new SymbolSequenceRenderer(scene);
    this.nodePositions = new Map();
  }

  public render(state: LevelSelectState, options: LevelSelectRenderOptions = {}): void {
    this.graphics.clear();
    this.nodePositions.clear();

    const offset = this.computeOffset(state.graph.nodes);

    for (const edge of state.graph.edges) {
      this.renderEdge(edge, state.graph.nodes, offset);
    }

    this.updateBridgeLabels(state.graph.edges, state.graph.nodes, offset, options.chapters, options.save);

    for (const node of state.graph.nodes) {
      this.renderNode(node, offset, options.chapters, options.save, options.levels);
    }

    const focusedNode = state.graph.nodes.find((node) => node.id === state.focusedNodeId);
    if (focusedNode !== undefined) {
      this.renderCursor(focusedNode, offset, options, state.mode.kind === "passwordSuccess");
    }

    this.updatePasswordUI(state, options.chapters, options.save, options.passwordInputFlash);

    this.graphics.setDepth(0);
  }

  public destroy(): void {
    this.graphics.destroy();
    this.statusBackground.destroy();
    this.statusText.destroy();
    this.symbolSequenceRenderer.destroy();
    for (const label of this.bridgeLabels.values()) {
      label.destroy();
    }
    this.bridgeLabels.clear();
  }

  private computeOffset(nodes: readonly LevelSelectNodeState[]): { x: number; y: number } {
    if (nodes.length === 0) {
      return { x: 0, y: 0 };
    }

    const xs = nodes.map((node) => node.position.x);
    const ys = nodes.map((node) => node.position.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const centerX = VIEWPORT_WIDTH / 2;
    const centerY = VIEWPORT_HEIGHT / 2;
    const graphCenterX = ((minX + maxX) * CELL_SIZE) / 2;
    const graphCenterY = ((minY + maxY) * CELL_SIZE) / 2;

    return {
      x: centerX - graphCenterX,
      y: centerY - graphCenterY,
    };
  }

  private getNodeScreenPos(
    node: LevelSelectNodeState,
    offset: { x: number; y: number },
  ): { x: number; y: number } {
    const pos = {
      x: offset.x + node.position.x * CELL_SIZE,
      y: offset.y + node.position.y * CELL_SIZE,
    };
    this.nodePositions.set(node.id, pos);
    return pos;
  }

  private renderNode(
    node: LevelSelectNodeState,
    offset: { x: number; y: number },
    chapters: readonly ChapterData[] | undefined,
    save: GameSave | undefined,
    levels: Readonly<Record<string, LevelData>> | undefined,
  ): void {
    if (node.type === "lock") {
      return;
    }

    const pos = this.getNodeScreenPos(node, offset);
    const color = this.resolveNodeColor(node, save);
    const half = NODE_SIZE / 2;

    this.graphics.fillStyle(color);
    this.graphics.fillRect(pos.x - half, pos.y - half, NODE_SIZE, NODE_SIZE);

    if (node.type === "level" || node.type === "ending") {
      this.renderGroupSquares(node, pos, chapters, save, levels);
    }
  }

  private resolveNodeColor(node: LevelSelectNodeState, save: GameSave | undefined): number {
    if (save !== undefined && node.type !== "lock" && save.progression.perfectedLevels.includes(node.id)) {
      return PALETTE.nodePerfected;
    }

    switch (node.status) {
      case "locked":
        return PALETTE.nodeLocked;
      case "unlocked":
        return PALETTE.nodeUnlocked;
      case "cleared":
        return PALETTE.nodeCleared;
      case "passed":
        return PALETTE.nodePassed;
      default:
        return PALETTE.nodeLocked;
    }
  }

  private renderGroupSquares(
    node: LevelSelectNodeState,
    pos: { x: number; y: number },
    chapters: readonly ChapterData[] | undefined,
    save: GameSave | undefined,
    levels: Readonly<Record<string, LevelData>> | undefined,
  ): void {
    if (chapters === undefined || save === undefined) {
      return;
    }

    const chapter = chapters.find((c) => c.id === node.chapterId);
    if (chapter === undefined) {
      return;
    }

    const groupKey = String(chapter.order);

    // Per-level eligibility: a level only shows the chapter-group square
    // if it actually contains at least one collectible whose group equals
    // this level's chapter number.
    const levelData = levels?.[node.id];
    const hasEligibleCollectible = levelData !== undefined &&
      levelData.collectibles.some((c) => c.group === groupKey);

    if (!hasEligibleCollectible) {
      return;
    }

    const chapterLevelNodes = chapters
      .filter((c) => c.id === node.chapterId)
      .flatMap((c) => c.levels);

    const anyLevelInChapterHasGroup = chapterLevelNodes.some((levelId) => {
      const groups = save.collectibles.completedGroupsByLevel?.[levelId];
      return groups !== undefined && groups.includes(groupKey);
    });

    if (!anyLevelInChapterHasGroup) {
      return;
    }

    const thisLevelGroups = save.collectibles.completedGroupsByLevel?.[node.id];
    const thisLevelHasGroup = thisLevelGroups !== undefined && thisLevelGroups.includes(groupKey);
    const squareColor = thisLevelHasGroup ? this.getChapterGroupColor(chapter.order) : PALETTE.groupGray;

    const squareX = pos.x - NODE_SIZE / 2 + GROUP_SQUARE_INSET - GROUP_SQUARE_CORNER_OFFSET;
    const squareY = pos.y + NODE_SIZE / 2 - GROUP_SQUARE_SIZE - GROUP_SQUARE_INSET + GROUP_SQUARE_CORNER_OFFSET;

    this.graphics.fillStyle(squareColor);
    this.graphics.fillRect(squareX, squareY, GROUP_SQUARE_SIZE, GROUP_SQUARE_SIZE);
  }

  private renderEdge(
    edge: LevelSelectEdgeState,
    nodes: readonly LevelSelectNodeState[],
    offset: { x: number; y: number },
  ): void {
    const fromNode = nodes.find((node) => node.id === edge.from);
    const toNode = nodes.find((node) => node.id === edge.to);
    if (fromNode === undefined || toNode === undefined) {
      return;
    }

    if (!edge.synthetic && (fromNode.type === "lock" || toNode.type === "lock")) {
      return;
    }

    const fromPos = this.getNodeScreenPos(fromNode, offset);
    const toPos = this.getNodeScreenPos(toNode, offset);

    if (edge.locked) {
      this.drawDashedLine(fromPos.x, fromPos.y, toPos.x, toPos.y, PALETTE.edgeLocked);
    } else {
      this.graphics.lineStyle(2, PALETTE.edgeUnlocked);
      this.graphics.beginPath();
      this.graphics.moveTo(fromPos.x, fromPos.y);
      this.graphics.lineTo(toPos.x, toPos.y);
      this.graphics.strokePath();
    }

    if (edge.synthetic) {
      const midX = (fromPos.x + toPos.x) / 2;
      const midY = (fromPos.y + toPos.y) / 2;
      const bridgeSquareSize = 6;
      const squareColor = edge.locked ? PALETTE.bridgeLockLocked : PALETTE.bridgeLockUnlocked;
      this.graphics.fillStyle(squareColor);
      this.graphics.fillRect(
        midX - bridgeSquareSize / 2,
        midY - bridgeSquareSize / 2,
        bridgeSquareSize,
        bridgeSquareSize,
      );
    }
  }

  private drawDashedLine(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: number,
  ): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) {
      return;
    }

    const nx = dx / dist;
    const ny = dy / dist;
    const dash = 4;
    const gap = 3;
    const seg = dash + gap;
    const count = Math.floor(dist / seg);

    this.graphics.lineStyle(1, color);

    for (let i = 0; i <= count; i += 1) {
      const start = i * seg;
      const end = Math.min(start + dash, dist);
      if (start >= dist) {
        break;
      }

      this.graphics.beginPath();
      this.graphics.moveTo(x1 + nx * start, y1 + ny * start);
      this.graphics.lineTo(x1 + nx * end, y1 + ny * end);
      this.graphics.strokePath();
    }
  }

  private renderCursor(
    node: LevelSelectNodeState,
    offset: { x: number; y: number },
    options: LevelSelectRenderOptions,
    successFlash: boolean,
  ): void {
    const pos = this.getNodeScreenPos(node, offset);
    const offsetX = options.cursorOffsetX ?? 0;
    const offsetY = options.cursorOffsetY ?? 0;
    const cx = pos.x + offsetX;
    const cy = pos.y + offsetY;
    const color = options.cursorRed
      ? PALETTE.cursorRed
      : successFlash
        ? PALETTE.cursorGreen
        : PALETTE.cursorNormal;

    this.graphics.fillStyle(color);

    const gap = 4;
    const markLen = 8;
    const thick = 2;
    const half = NODE_SIZE / 2;
    const outer = half + gap;

    this.drawCursorCorner(cx, cy, -1, -1, outer, markLen, thick);
    this.drawCursorCorner(cx, cy, 1, -1, outer, markLen, thick);
    this.drawCursorCorner(cx, cy, -1, 1, outer, markLen, thick);
    this.drawCursorCorner(cx, cy, 1, 1, outer, markLen, thick);
  }

  private updateBridgeLabels(
    edges: readonly LevelSelectEdgeState[],
    nodes: readonly LevelSelectNodeState[],
    offset: { x: number; y: number },
    chapters: readonly ChapterData[] | undefined,
    save: GameSave | undefined,
  ): void {
    const visibleIds = new Set(
      edges.filter((edge) => edge.synthetic).map((edge) => `${edge.from}->${edge.to}`),
    );

    for (const [id, label] of this.bridgeLabels) {
      if (!visibleIds.has(id)) {
        label.destroy();
        this.bridgeLabels.delete(id);
      }
    }

    if (chapters === undefined || save === undefined) {
      return;
    }

    for (const edge of edges) {
      if (!edge.synthetic) {
        continue;
      }

      const fromNode = nodes.find((node) => node.id === edge.from);
      const toNode = nodes.find((node) => node.id === edge.to);
      if (fromNode === undefined || toNode === undefined) {
        continue;
      }

      const toChapter = chapters.find((c) => c.id === toNode.chapterId);
      if (toChapter?.unlock === undefined) {
        continue;
      }

      const gate = this.computeCollectibleGate(toChapter, chapters, save);
      if (gate === null || !gate.hasAnyCompletedGroupInPreviousChapter) {
        continue;
      }

      const fromPos = this.getNodeScreenPos(fromNode, offset);
      const toPos = this.getNodeScreenPos(toNode, offset);
      const midX = (fromPos.x + toPos.x) / 2;
      const midY = (fromPos.y + toPos.y) / 2;

      const edgeId = `${edge.from}->${edge.to}`;
      let label = this.bridgeLabels.get(edgeId);
      if (label === undefined) {
        label = this.scene.add.bitmapText(0, 0, NOTE_OPTIONS_FONT_KEY, "", NOTE_OPTIONS_FONT_SIZE);
        label.setOrigin(0.5, 1);
        this.bridgeLabels.set(edgeId, label);
      }

      const fromChapter = chapters.find((c) => c.id === fromNode.chapterId);
      const labelTint = fromChapter !== undefined
        ? this.getChapterGroupColor(fromChapter.order)
        : 0xffffff;
      const labelText = `${gate.current}/${gate.required}`;
      const labelWidth = labelText.length * NOTE_OPTIONS_FONT_SIZE;
      const chipX = midX - (labelWidth / 2) - BRIDGE_PROGRESS_CHIP_SIZE - 3;
      const chipY = midY - 12;

      this.graphics.fillStyle(fromChapter !== undefined ? this.getChapterGroupColor(fromChapter.order) : 0xffffff);
      this.graphics.fillRect(
        chipX,
        chipY - BRIDGE_PROGRESS_CHIP_SIZE / 2,
        BRIDGE_PROGRESS_CHIP_SIZE,
        BRIDGE_PROGRESS_CHIP_SIZE,
      );

      label.setText(labelText);
      label.setTint(labelTint);
      label.setPosition(midX + 4, midY - 8);
      label.setVisible(true);
    }
  }

  private computeCollectibleGate(
    chapter: ChapterData,
    chapters: readonly ChapterData[],
    save: GameSave,
  ): { current: number; required: number; hasAnyCompletedGroupInPreviousChapter: boolean } | null {
    const chapterIndex = chapters.findIndex((c) => c.id === chapter.id);
    if (chapterIndex <= 0) {
      return null;
    }

    const previousChapter = chapters[chapterIndex - 1]!;
    const prevChapterNumber = String(previousChapter.order);
    let current = 0;

    for (const levelId of previousChapter.levels) {
      const groups = save.collectibles.completedGroupsByLevel?.[levelId] ?? [];
      if (groups.includes(prevChapterNumber)) {
        current += 1;
      }
    }

    const required = chapter.unlock?.requiredPreviousChapterCompletedLevels ?? 0;
    return {
      current,
      required,
      hasAnyCompletedGroupInPreviousChapter: current > 0,
    };
  }

  private getChapterGroupColor(chapterOrder: number): number {
    return CHAPTER_GROUP_COLORS[(chapterOrder - 1) % CHAPTER_GROUP_COLORS.length]!;
  }

  private updatePasswordUI(
    state: LevelSelectState,
    chapters: readonly ChapterData[] | undefined,
    save: GameSave | undefined,
    passwordInputFlash: boolean = false,
  ): void {
    if (state.mode.kind === "passwordSuccess") {
      const mode = state.mode;
      const chapter = chapters?.find((c) => c.id === mode.chapterId);
      const hintSequence = chapter?.unlock?.hintDisplay?.symbolSequence ?? [];
      const guessResult = buildPasswordGuessResult(save, hintSequence);
      this.symbolSequenceRenderer.setBackgroundOverride(PALETTE.passwordSuccessLightGreen, 0.5);
      this.symbolSequenceRenderer.setVisible(true);
      this.symbolSequenceRenderer.renderFromSequence(hintSequence, {
        subLabel: guessResult.allUnknown ? "" : guessResult.text,
        instructionText: "",
        textTopY: PASSWORD_SEQUENCE_TOP_Y,
      });
      this.renderPasswordStatus("MATCHED");
      return;
    }

    if (state.mode.kind === "passwordLock") {
      const mode = state.mode;
      const chapter = chapters?.find((c) => c.id === mode.chapterId);
      const hintSequence = chapter?.unlock?.hintDisplay?.symbolSequence ?? [];
      const guessResult = buildPasswordGuessResult(save, hintSequence);

      if (mode.failed) {
        this.symbolSequenceRenderer.setBackgroundOverride(PALETTE.cursorRed, 0.5);
      } else if (passwordInputFlash) {
        this.symbolSequenceRenderer.setBackgroundOverride(PALETTE.passwordFlashBlue, 0.5);
      } else {
        this.symbolSequenceRenderer.clearBackgroundOverride();
      }

      this.symbolSequenceRenderer.setVisible(true);
      this.symbolSequenceRenderer.renderFromSequence(hintSequence, {
        subLabel: guessResult.allUnknown ? "" : guessResult.text,
        instructionText: "",
        textTopY: PASSWORD_SEQUENCE_TOP_Y,
      });

      const status = mode.failed ? "INCORRECT" : "CHECK THE SIGNALS";
      this.renderPasswordStatus(status);
      return;
    }

    this.symbolSequenceRenderer.clearBackgroundOverride();
    this.symbolSequenceRenderer.clearTransientText();
    this.symbolSequenceRenderer.setVisible(false);
    this.statusText.setVisible(false);
    this.statusBackground.setVisible(false);
  }

  private renderPasswordStatus(text: string): void {
    this.statusText.render(text, {
      centerX: VIEWPORT_WIDTH / 2,
      y: PASSWORD_STATUS_Y,
      verticalAlign: "top",
    });
    this.statusText.setVisible(true);

    const bounds = this.statusText.getLastBounds();
    if (bounds === null) {
      this.statusBackground.setVisible(false);
      return;
    }

    this.statusBackground.setSize(
      bounds.width + PASSWORD_STATUS_BG_PADDING_X * 2,
      bounds.height + PASSWORD_STATUS_BG_PADDING_Y * 2,
    );
    this.statusBackground.setPosition(
      bounds.x + bounds.width / 2,
      bounds.y + bounds.height / 2,
    );
    this.statusBackground.setVisible(true);
  }

  private drawCursorCorner(
    cx: number,
    cy: number,
    dirX: number,
    dirY: number,
    outer: number,
    markLen: number,
    thick: number,
  ): void {
    const baseX = cx + dirX * outer;
    const baseY = cy + dirY * outer;
    const hStartX = dirX === -1 ? baseX - markLen : baseX;
    const hStartY = dirY === -1 ? baseY - thick : baseY;
    const vStartX = dirX === -1 ? baseX - thick : baseX;
    const vStartY = dirY === -1 ? baseY - markLen : baseY;

    this.graphics.fillRect(hStartX, hStartY, markLen, thick);
    this.graphics.fillRect(vStartX, vStartY, thick, markLen);
  }
}

function buildPasswordGuessResult(
  save: GameSave | undefined,
  hintSequence: readonly string[],
): ReturnType<typeof buildGuessSequence> {
  if (save === undefined) {
    return { text: "", allUnknown: true };
  }

  const noteState: NoteState = {
    guesses: { ...save.symbols.notes },
  };

  return buildGuessSequence(noteState, hintSequence);
}
